import { getCurrentChatInput, useAttachments, useSetChatInput } from '@renderer/chat'
import { useSubmitMessage } from '@renderer/chat/hooks'
import { GlobalEvents } from '@highlight/ipc'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'jotai'
import { useCallback } from 'react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { stagedActionEditStatesAtom, type ActionEditState } from '../atoms'
import { useOpenActionChatDrawer } from '../views/action-view/use-action-chat-drawer'
import { STAGED_ACTIONS_QUERY_KEY, stagedActionExpandedQueryKey } from './staged-actions/utils'
import { useStagedAction } from './use-staged-action'
import { useStagedActionStatus, type StagedAction } from './use-staged-actions'

/**
 * Submits the scoped chat input as an edit to the staged action.
 *
 * Actions with a backend conversation get the edit as a real chat message on
 * that conversation (the model regenerates the draft as part of the turn), and
 * the Action View's chat drawer opens on that conversation (HIG-5977): the
 * drawer's chrome walks Chatting → Modify → Updated as the edit state
 * advances. The drawer opens *in place* — the open Action View entry flips
 * into its chat state rather than a new view being pushed. Actions without a
 * conversation (freshly generated
 * drafts) fall back to the legacy /staged-actions/:id/edit endpoint, which
 * manages the edit state itself — no conversation, so no drawer.
 *
 * An action whose invoke failed takes the same route: nothing shipped, so the
 * draft is still editable, and chatting about it is how the user fixes it
 * before Retry. It just rides the `stagedActionRepair` framing instead.
 */
export function useSubmitStagedActionMessage(
  stagedActionId: string | undefined,
  editStagedAction: (id: string, prompt: string) => Promise<void>,
) {
  const setChatInput = useSetChatInput()
  const submitMessage = useSubmitMessage()
  const queryClient = useQueryClient()
  const store = useStore()
  const attachments = useAttachments()
  // Resolve via useStagedAction rather than the list cache alone: freshly
  // chat-created actions often aren't in ['staged-actions'] yet, and missing
  // the conversationId here would wrongly route the edit to the legacy
  // /staged-actions/:id/edit endpoint.
  const { action } = useStagedAction({ actionId: stagedActionId })
  // The placeholder keeps hook order stable while the action loads; its derived
  // status is only read inside the callback, by which point it has resolved.
  const { status } = useStagedActionStatus(action ?? { id: stagedActionId ?? '' })
  // The scoped composer only renders while an Action View is on top (see
  // useChatInputMode), so opening the drawer targets exactly this view — and
  // is a no-op when it's already open (follow-ups).
  const openActionChatDrawer = useOpenActionChatDrawer()

  return useCallback(async () => {
    if (!stagedActionId) return
    const input = getCurrentChatInput()
    if (!input.trim()) return
    setChatInput('')

    const conversationId =
      action?.conversationId ?? store.get(stagedActionEditStatesAtom)[stagedActionId]?.conversationId ?? null
    if (!conversationId) {
      await editStagedAction(stagedActionId, input)
      return
    }

    const updateStatus = (state: ActionEditState) => {
      store.set(stagedActionEditStatesAtom, (prev) => ({ ...prev, [stagedActionId]: state }))
    }
    updateStatus({ status: 'pending', message: null, conversationId })

    // submitMessage resolves after the turn's terminal `done`, but it never
    // rejects on chat errors — it surfaces them in the transcript, which stays
    // closed here. 'chat:message:sent' firing for this conversation (emitted
    // only after a clean stream) is the success signal.
    let sent = false
    const onSent = (payload: { conversationId?: string }) => {
      if (payload?.conversationId === conversationId) sent = true
    }
    GlobalEvents.on('chat:message:sent', onSent)

    try {
      const submitPromise = submitMessage(input, {
        conversationId,
        // The submit itself must not claim the globally-active chat — the
        // drawer revealed below is what makes this conversation visible, and
        // its own mount sets the active chat (see SendMessageOptions' doc).
        preserveActiveChat: true,
        // A failed action gets the repair framing instead of the edit one: the
        // model is told the draft errored and to fix it so Retry can work,
        // rather than to apply a change to a draft it has no reason to distrust.
        attachedContext: [{ type: status === 'error' ? 'stagedActionRepair' : 'stagedActionEdit', stagedActionId }],
        // The draft pill in the attachment store is client-side display only
        // (finalize rejects unknown pending types) — the edit target rides on
        // the stagedActionEdit context instead. Real uploads pass through.
        attachments: attachments.filter((a) => a.pending?.type !== 'stagedAction'),
        // Stamps the draft chip onto the transcript's user message so the
        // conversation reads correctly if it's opened later.
        stagedActionAttachment: action
          ? {
              type: 'stagedAction',
              actionId: action.id,
              title: action.view?.title ?? action.title,
              actionType: action.actionType ?? '',
              toolSet: action.toolSet,
              view: action.view,
            }
          : undefined,
      })

      // Slide the chat drawer up in place: the thread over the still-visible
      // action (Chatting), advancing to Modify while the edit streams.
      openActionChatDrawer()

      await submitPromise
    } finally {
      GlobalEvents.off('chat:message:sent', onSent)
    }

    if (!sent) {
      updateStatus({ status: 'error', message: 'Edit could not be applied', conversationId })
      return
    }

    // Mark edited locally so invoke metrics see wasEdited before refetch settles.
    queryClient.setQueryData<Array<StagedAction>>(
      [STAGED_ACTIONS_QUERY_KEY],
      (old) =>
        old?.map((a) => (a.id === stagedActionId ? { ...a, metadata: { ...a.metadata, userEdited: 'true' } } : a)) ??
        [],
    )
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'edit',
      ...(action && stagedActionEventProperties(action)),
      actionId: stagedActionId,
      editMethod: 'prompt',
    })
    // Hold Updating until the regenerated draft is actually in cache, so the
    // view never flashes the stale draft between success and refetch.
    await queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
    queryClient.invalidateQueries({ queryKey: stagedActionExpandedQueryKey(stagedActionId) })
    updateStatus({ status: 'success', message: null, conversationId })
  }, [
    stagedActionId,
    action,
    attachments,
    editStagedAction,
    setChatInput,
    submitMessage,
    queryClient,
    status,
    store,
    openActionChatDrawer,
  ])
}
